
import Vue from 'vue'
import { mapActions } from 'vuex'

interface ComponentData {
  images: Array<{
    src: string;
    fallback: string;
    alt: string;
    provider: string;
  }>;
  popupsEnabled: boolean;
  broadcastChannel: BroadcastChannel | null;
  storagePolling: number | null;
  lastCheckedTimestamp: number;
}

export default Vue.extend({
  props: {
    type: {
      type: String,
      default: 'login'
    }
  },
  data(): ComponentData {
    return {
      images: [
        {
          src: 'logos/Google_Logo.webp',
          fallback: 'logos/Google_Logo.png',
          alt: 'google-logo',
          provider: 'google'
        },
        {
          src: 'logos/Facebook_Logo.webp',
          fallback: 'logos/Facebook_Logo.png',
          alt: 'facebook-logo',
          provider: 'facebook'
        },
        {
          src: 'logos/apple_logo.webp',
          fallback: 'logos/apple_logo.png',
          alt: 'apple-logo',
          provider: 'apple'
        }
      ],
      popupsEnabled: true,
      broadcastChannel: null,
      storagePolling: null,
      lastCheckedTimestamp: Date.now()
    }
  },
  mounted() {
    window.addEventListener('message', this.handlePopupMessage)

    // Listen for localStorage changes (Facebook fallback)
    window.addEventListener('storage', this.handleStorageMessage)

    // Listen for BroadcastChannel (modern browsers fallback)
    if (typeof BroadcastChannel !== 'undefined') {
      this.broadcastChannel = new BroadcastChannel('sso_login')
      this.broadcastChannel.addEventListener('message', this.handleBroadcastMessage)
    }

    // Poll localStorage as final fallback
    this.storagePolling = window.setInterval(this.checkStorageForToken, 1000)
  },
  beforeDestroy() {
    window.removeEventListener('message', this.handlePopupMessage)
    window.removeEventListener('storage', this.handleStorageMessage)

    if (this.broadcastChannel) {
      this.broadcastChannel.close()
    }
    if (this.storagePolling) {
      window.clearInterval(this.storagePolling)
    }
  },
  methods: {
    ...mapActions(
      'user',
      ['loginSso', 'loginSsoUser']
    ),
    loginWithPopup(provider: string) {
      const width = 500
      const height = 600
      const left = (window.screen.width / 2) - (width / 2)
      const top = (window.screen.height / 2) - (height / 2)

      const url = `${this.$config.apiURL}/api/Authentication/external-login/${provider}`

      try {
        const popup = window.open(
          url,
          `${provider}Login`,
          `width=${width},height=${height},top=${top},left=${left}`
        )

        if (!popup) {
          this.popupsEnabled = false
          return
        }

        setTimeout(() => {
          try {
            this.popupsEnabled = !(popup.closed || popup.outerWidth === 0 || popup.outerHeight === 0)
          } catch (e) {
            this.popupsEnabled = true
          }
        }, 100)

      } catch (error) {
        this.popupsEnabled = false
        return
      }

      this.$emit('login', true)
    },
    handlePopupMessage(event: MessageEvent) {
      const webUrl = this.$config.webUrl
      if (event.origin === webUrl && event.data.token) {
        this.loginSsoUser(event.data.token)
        this.$emit('confirmationMade')
      }
    },
    handleStorageMessage(event: StorageEvent) {
      if (event.key === 'sso_login_token' && event.newValue) {
        const timestamp = localStorage.getItem('sso_login_timestamp')
        if (timestamp && Date.now() - parseInt(timestamp) < 5000) { // Check that the request is recent, security and sanity check
          this.loginSsoUser(event.newValue)
          this.$emit('confirmationMade')
        }
      }
    },
    handleBroadcastMessage(event: MessageEvent) {
      if (event.data && event.data.loggedIn && event.data.token) {
        this.loginSsoUser(event.data.token)
        this.$emit('confirmationMade')
      }
    },
    checkStorageForToken() {
      const token = localStorage.getItem('sso_login_token')
      const timestamp = localStorage.getItem('sso_login_timestamp')

      if (token && timestamp) {
        const tokenTime = parseInt(timestamp)
        if (tokenTime > this.lastCheckedTimestamp && Date.now() - tokenTime < 5000) { // Check that the request is recent, security and sanity check
          this.loginSsoUser(token)
          this.$emit('confirmationMade')
          this.lastCheckedTimestamp = Date.now()
        }
      }
    }
  }
})
